import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Compass, Lock, Check, Loader2, MapPin, CarFront, UtensilsCrossed } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/* Atlas — the CityDex. Landmarks, rare wheels and food spots to log. The bridge
   fires scans in-game; in preview you can scan manually to see the flow. */

const CAT_META = {
  landmarks: { label: "Landmarks", Icon: MapPin, color: "#38bdf8" },
  wheels: { label: "Wheels", Icon: CarFront, color: "#f59e0b" },
  bites: { label: "Bites", Icon: UtensilsCrossed, color: "#4ade80" },
};
const DEMO_UNLOCKED = { landmarks: [{ id: "cityhall", name: "City Hall", at: "" }], bites: [{ id: "donut", name: "Rusty Donut", at: "" }] };

export default function AtlasApp({ demo, serverId, token, accent, sfx }) {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("landmarks");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    if (demo) { setData({ catalog: null, unlocked: DEMO_UNLOCKED, count: 2, total: 22, pct: 9, bounty: 25, _demoCatalog: true }); return; }
    api.get(`/nc/servers/${serverId}/atlas/me`, { params: { token } })
      .then((r) => setData(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [demo, serverId, token]);
  useEffect(() => { load(); }, [load]);

  const catalog = data?._demoCatalog ? null : data?.catalog;
  const [demoCatalog, setDemoCatalog] = useState(null);
  useEffect(() => {
    if (!demo || demoCatalog) return;
    // demo mode still wants the real catalog for the grid — public shape, safe to fetch
    api.get(`/nc/servers/${serverId}/atlas/me`, { params: { token } }).then((r) => setDemoCatalog(r.data.catalog)).catch(() => {});
  }, [demo, demoCatalog, serverId, token]);
  const catSource = catalog || demoCatalog;

  const scan = async (entry) => {
    if (busy) return;
    setBusy(entry); sfx?.keyTick();
    try {
      if (demo) { toast.success(`Preview: ${entry} logged (demo — in-game the bridge scans for you)`); return; }
      const r = await api.post(`/nc/servers/${serverId}/atlas/scan`, { category: cat, entry }, { params: { token } });
      r.data.new ? toast.success(r.data.message) : toast(r.data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!data) return <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-white/50" /></div>;

  const unlockedIds = new Set((data.unlocked[cat] || []).map((e) => e.id));
  return (
    <div data-testid="atlas-app" className="flex-1 flex flex-col gap-3 overflow-y-auto pb-4" style={{ scrollbarWidth: "none" }}>
      <div className="flex items-center gap-2">
        <p className="font-sans font-bold text-white text-lg flex-1">Atlas</p>
        <span className="font-mono text-[9px] text-white/50">{data.count}/{data.total}</span>
      </div>

      {/* progress */}
      <div className="border border-white/10 bg-white/[0.03] rounded-2xl p-3.5">
        <div className="flex items-center justify-between mb-2">
          <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-white/40 flex items-center gap-1.5"><Compass className="w-3 h-3" style={{ color: accent }} /> Bornwood completion</p>
          <p className="font-mono text-[10px] font-bold" style={{ color: accent }}>{data.pct}%</p>
        </div>
        <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${data.pct}%`, backgroundColor: accent }} />
        </div>
        <p className="font-mono text-[7px] text-white/30 mt-2 uppercase tracking-[0.15em]">new discoveries pay ${data.bounty}</p>
      </div>

      {/* category tabs */}
      <div className="flex gap-1.5">
        {Object.entries(CAT_META).map(([id, m]) => (
          <button key={id} data-testid={`atlas-cat-${id}`} onClick={() => { setCat(id); sfx?.keyTick(); }}
            className={`flex-1 py-2 rounded-xl font-mono text-[8px] uppercase tracking-[0.12em] border transition-colors flex items-center justify-center gap-1 ${cat === id ? "text-black font-bold" : "border-white/10 text-white/50"}`}
            style={cat === id ? { backgroundColor: m.color, borderColor: m.color } : {}}>
            <m.Icon className="w-3 h-3" /> {m.label}
          </button>
        ))}
      </div>

      {/* grid */}
      <div className="grid grid-cols-2 gap-2">
        {(catSource?.[cat] || []).map((e) => {
          const has = unlockedIds.has(e.id);
          return (
            <button key={e.id} data-testid={`atlas-entry-${e.id}`} onClick={() => !has && scan(e.id)} disabled={has || busy === e.id}
              className={`border rounded-2xl p-3 text-left transition-colors ${has ? "border-emerald-500/30 bg-emerald-500/[0.06]" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
              <div className="flex items-center justify-between">
                {has ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Lock className="w-3.5 h-3.5 text-white/25" />}
                {busy === e.id && <Loader2 className="w-3 h-3 animate-spin text-white/50" />}
              </div>
              <p className={`font-mono text-[9px] font-bold mt-2 ${has ? "text-white" : "text-white/40"}`}>{has ? e.name : "???"}</p>
              <p className="font-mono text-[7px] text-white/30 mt-0.5 leading-relaxed">{e.hint}</p>
            </button>
          );
        })}
      </div>
      <p className="font-mono text-[7px] text-white/25 text-center uppercase tracking-[0.2em] mt-1">in-game the bridge scans as you explore — tap an entry to log it in preview</p>
    </div>
  );
}
